import { NgClass } from '@angular/common';
import { Component, computed, effect, ElementRef, HostListener, inject, input, output, signal, untracked } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { VirusSprayCategory } from '@app/shared/components/disease-overview-table.component/models/models';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';

interface ProtectionTypeDefinition {
  iconPath: string;
  translationKey: string;
  type: VirusSprayCategory;
}

export interface ProtectionTypeDetail {
  status: 'active' | 'expired' | 'unused';
  latestAppliedOn?: Date;
  protectsUntil?: Date;
  protectingDays?: number;
  remainingDays?: number;
  daysSinceProtected?: number;
}

const protectionTypeDefinitions: ProtectionTypeDefinition[] = [
  {
    type: VirusSprayCategory.Oil,
    translationKey: 'virusMonitor.riskChart.oilProtection',
    iconPath: 'assets/icons/oil_percent.svg',
  },
  {
    type: VirusSprayCategory.Pyrethroid,
    translationKey: 'virusMonitor.riskChart.pyrethroidProtection',
    iconPath: 'assets/icons/pyrethroids_percent.svg',
  },
  {
    type: VirusSprayCategory.Systemic,
    translationKey: 'virusMonitor.riskChart.systemicProtection',
    iconPath: 'assets/icons/systemic_percent.svg',
  },
];

@Component({
  selector: 'app-virus-monitor-protection-type-selector',
  standalone: true,
  templateUrl: './virus-monitor-protection-type-selector.component.html',
  styleUrl: './virus-monitor-protection-type-selector.component.scss',
  imports: [NgClass, MatButton, TranslatePipe],
})
export class VirusMonitorProtectionTypeSelectorComponent {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly localeService = inject(LocaleService);

  readonly availableProtectionTypes = input.required<VirusSprayCategory[]>();
  readonly selectedProtectionTypes = input.required<VirusSprayCategory[]>();
  readonly disabled = input(false);
  readonly warningProtectionTypes = input<VirusSprayCategory[]>([]);
  readonly protectionTypeDetails = input<Partial<Record<VirusSprayCategory, ProtectionTypeDetail>>>({});
  readonly protectionTypesChanged = output<VirusSprayCategory[]>();

  protected readonly isOpen = signal(false);
  protected readonly draftSelection = signal<VirusSprayCategory[]>([]);
  protected readonly protectionTypes = protectionTypeDefinitions;

  protected readonly selectedIcons = computed(() =>
    this.protectionTypes.filter((definition) => this.selectedProtectionTypes().includes(definition.type)),
  );
  protected readonly availableCount = computed(() => this.availableProtectionTypes().length);
  protected readonly canSave = computed(() => {
    const availableProtectionTypes = this.availableProtectionTypes();
    const draftSelection = this.draftSelection();

    if (availableProtectionTypes.length > 0 && draftSelection.length === 0) {
      return false;
    }

    return !hasSameSelection(draftSelection, this.selectedProtectionTypes());
  });

  constructor() {
    effect(() => {
      const selectedProtectionTypes = this.selectedProtectionTypes();
      if (untracked(() => this.isOpen())) {
        return;
      }

      this.draftSelection.set([...selectedProtectionTypes]);
    });
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen() || this.host.nativeElement.contains(event.target as Node)) {
      return;
    }

    this.close();
  }

  protected toggleOpen(): void {
    if (this.disabled()) {
      return;
    }

    if (this.isOpen()) {
      this.close();
      return;
    }

    this.draftSelection.set([...this.selectedProtectionTypes()]);
    this.isOpen.set(true);
  }

  protected close(): void {
    this.isOpen.set(false);
    this.draftSelection.set([...this.selectedProtectionTypes()]);
  }

  protected toggleProtectionType(type: VirusSprayCategory): void {
    if (!this.isAvailable(type)) {
      return;
    }

    const currentDraft = this.draftSelection();
    this.draftSelection.set(
      currentDraft.includes(type)
        ? currentDraft.filter((currentType) => currentType !== type)
        : [...currentDraft, type].sort(sortProtectionTypes),
    );
  }

  protected save(): void {
    if (this.disabled() || !this.canSave()) {
      return;
    }

    this.protectionTypesChanged.emit([...this.draftSelection()].sort(sortProtectionTypes));
    this.isOpen.set(false);
  }

  protected isAvailable(type: VirusSprayCategory): boolean {
    return this.availableProtectionTypes().includes(type);
  }

  protected isSelected(type: VirusSprayCategory): boolean {
    return this.draftSelection().includes(type);
  }

  protected hasWarning(type: VirusSprayCategory): boolean {
    return this.warningProtectionTypes().includes(type);
  }

  protected detailFor(type: VirusSprayCategory): ProtectionTypeDetail {
    return this.protectionTypeDetails()[type] ?? { status: this.isAvailable(type) ? 'expired' : 'unused' };
  }

  protected statusLabelKey(type: VirusSprayCategory): string {
    return `virusMonitor.protectionTypes.status.${this.detailFor(type).status}`;
  }

  protected statusClass(type: VirusSprayCategory): Record<string, boolean> {
    const status = this.detailFor(type).status;
    return {
      'selector-option__status--active': status === 'active',
      'selector-option__status--expired': status === 'expired',
      'selector-option__status--unused': status === 'unused',
    };
  }

  protected descriptionKey(type: VirusSprayCategory): string {
    const detail = this.detailFor(type);

    if (detail.status === 'active' && !hasReliableActiveDescription(detail)) {
      return 'virusMonitor.protectionTypes.description.activeUnknown';
    }

    if (detail.status === 'expired' && !hasReliableExpiredDescription(detail)) {
      return 'virusMonitor.protectionTypes.description.expiredUnknown';
    }

    return `virusMonitor.protectionTypes.description.${detail.status}`;
  }

  protected descriptionParams(type: VirusSprayCategory): Record<string, string> {
    const detail = this.detailFor(type);

    return {
      latestAppliedOn: this.formatDate(detail.latestAppliedOn),
      protectsUntil: this.formatDate(detail.protectsUntil),
      remainingDays: String(detail.remainingDays ?? 0),
      daysSinceProtected: String(detail.daysSinceProtected ?? 0),
    };
  }

  private formatDate(date: Date | undefined): string {
    if (!date) {
      return '-';
    }

    return new Intl.DateTimeFormat(this.localeService.getLocale(), { day: 'numeric', month: 'short' }).format(date);
  }
}

function hasSameSelection(left: VirusSprayCategory[], right: VirusSprayCategory[]): boolean {
  const leftSorted = [...left].sort(sortProtectionTypes);
  const rightSorted = [...right].sort(sortProtectionTypes);

  return leftSorted.length === rightSorted.length && leftSorted.every((type, index) => type === rightSorted[index]);
}

function sortProtectionTypes(left: VirusSprayCategory, right: VirusSprayCategory): number {
  return protectionTypeDefinitions.findIndex((definition) => definition.type === left)
    - protectionTypeDefinitions.findIndex((definition) => definition.type === right);
}

function hasReliableActiveDescription(detail: ProtectionTypeDetail): boolean {
  return detail.latestAppliedOn != null && detail.remainingDays != null;
}

function hasReliableExpiredDescription(detail: ProtectionTypeDetail): boolean {
  return detail.latestAppliedOn != null && detail.daysSinceProtected != null;
}
